



import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const StudentAssignmentSubmit = () => {
  const [assignments, setAssignments] = useState([]);
  const [selectedAssignment, setSelectedAssignment] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchingAssignments, setFetchingAssignments] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  const [dragActive, setDragActive] = useState(false);
  const { role, token } = useContext(AuthContext);

  const navigate = useNavigate();

  // Fetch assignments for student
  useEffect(() => {
    const fetchAssignments = async () => {
      if (!token) return;
      setFetchingAssignments(true);
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        const res = await axios.get(`${apiUrl}/api/assignments`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Get current date (start of today)
        const currentDate = new Date();
        currentDate.setHours(0, 0, 0, 0);

        // Filter assignments that are overdue (due date < current date)
        const overdueAssignments = res.data.filter((assignment) => {
          const dueDate = new Date(assignment.dueDate);
          dueDate.setHours(0, 0, 0, 0);

          const currentDate = new Date();
          currentDate.setHours(0, 0, 0, 0);

          // Calculate difference in days
          const timeDiff = currentDate - dueDate;
          const daysDiff = Math.floor(timeDiff / (1000 * 60 * 60 * 24));

          // Return assignments overdue by exactly 1 day OR active (due today or future)
          return daysDiff === 1 || dueDate >= currentDate;
        });

        if (role === "student") setAssignments(overdueAssignments);
        else setAssignments(res.data);
      } catch (err) {
        console.error('Error fetching assignments:', err);
        setSnackbar({ open: true, message: 'Failed to load assignments', severity: 'error' });
      } finally {
        setFetchingAssignments(false);
      }
    };
    fetchAssignments();
  }, [token]);

  const validateFile = (file, input = null) => {
    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      setSnackbar({
        open: true,
        message: 'File size must be less than 10MB',
        severity: 'error'
      });
      if (input) input.value = ''; // Clear the file input if provided
      return false;
    }

    // Check file type
    const allowedTypes = ['pdf', 'doc', 'docx', 'png', 'jpg', 'jpeg', 'ppt', 'pptx'];
    const fileExtension = file.name.split('.').pop().toLowerCase();
    if (!allowedTypes.includes(fileExtension)) {
      setSnackbar({
        open: true,
        message: 'Only PDF, DOC, DOCX, PNG, JPG, PPT, and PPTX files are allowed',
        severity: 'error'
      });
      if (input) input.value = ''; // Clear the file input if provided
      return false;
    }

    return true;
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (validateFile(selectedFile, e.target)) {
        setFile(selectedFile);
      }
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (validateFile(droppedFile)) {
        setFile(droppedFile);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedAssignment) {
      setSnackbar({ open: true, message: 'Please select an assignment', severity: 'warning' });
      return;
    }
    if (!file) {
      setSnackbar({ open: true, message: 'Please upload a file', severity: 'warning' });
      return;
    }

    try {
      setLoading(true);
      const formData = new FormData();
      formData.append('assignmentId', selectedAssignment);
      formData.append('file', file);

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      const res = await axios.post(`${apiUrl}/api/assignments/submit`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setSnackbar({ open: true, message: res.data.message, severity: 'success' });

      // Reset form
      setSelectedAssignment('');
      setFile(null);

      // Redirect to assignments list after submission
      setTimeout(() => navigate('/assignments'), 2000);
    } catch (err) {
      console.error('Error submitting assignment:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Error submitting assignment',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const closeSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getSelectedAssignment = () => {
    return assignments.find(a => a._id === selectedAssignment);
  };

  return (
    <div className="assignment-submit-container">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        
        .assignment-submit-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          position: relative;
          overflow: hidden;
          padding: 2rem 1rem;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
        }

        .assignment-submit-container::before {
          content: '';
          position: absolute;
          top: 15%;
          right: 10%;
          width: 120px;
          height: 120px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.08);
          animation: float 4s ease-in-out infinite;
        }

        .assignment-submit-container::after {
          content: '';
          position: absolute;
          bottom: 20%;
          left: 12%;
          width: 90px;
          height: 90px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.05);
          animation: float 3.5s ease-in-out infinite;
          animation-delay: 1.8s;
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-15px) rotate(8deg); }
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes slideInFromBottom {
          from { 
            opacity: 0; 
            transform: translateY(100px); 
          }
          to { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }

        .pulse-element {
          position: absolute;
          top: 35%;
          right: 30%;
          width: 60px;
          height: 60px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.06);
          animation: pulse 3s ease-in-out infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.7; }
        }

        .submit-wrapper {
          max-width: 600px;
          margin: 0 auto;
          position: relative;
          z-index: 1;
        }

        .header-section {
          text-align: center;
          margin-bottom: 3rem;
        }

        .header-chip {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          background: rgba(255, 255, 255, 0.15);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 50px;
          font-size: 0.9rem;
          font-weight: 500;
          margin-bottom: 1.5rem;
          backdrop-filter: blur(10px);
        }

        .main-title {
          font-size: clamp(2.2rem, 4vw, 3rem);
          font-weight: 900;
          color: white;
          margin-bottom: 0.5rem;
          text-shadow: 0 4px 20px rgba(0,0,0,0.3);
          animation: slideIn 0.8s ease-out;
        }

        .gradient-text {
          background: linear-gradient(45deg, #FFD700, #FF6B6B, #4ECDC4);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .subtitle {
          color: rgba(255, 255, 255, 0.9);
          font-weight: 300;
          font-size: 1rem;
          animation: slideIn 0.8s ease-out 0.2s both;
        }

        .form-container {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          padding: 3rem;
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 20px 40px rgba(0,0,0,0.1);
          animation: slideIn 0.6s ease-out 0.4s both;
          position: relative;
          overflow: hidden;
        }

        .form-container::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #667eea, #764ba2, #f093fb, #f5576c);
          border-radius: 24px 24px 0 0;
        }

        .form-title {
          display: flex;
          align-items: center;
          gap: 1rem;
          margin-bottom: 2rem;
        }

        .form-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(45deg, #667eea 30%, #764ba2 90%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
        }

        .form-title-text {
          font-size: 1.5rem;
          font-weight: 700;
          color: #1f2937;
        }

        .loading-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 4rem 2rem;
          color: #6b7280;
        }

        .loading-spinner {
          width: 48px;
          height: 48px;
          border: 4px solid rgba(102, 126, 234, 0.2);
          border-top: 4px solid #667eea;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin-bottom: 1.5rem;
        }

        .loading-text {
          font-size: 1.1rem;
          font-weight: 500;
        }

        .form-group {
          margin-bottom: 2rem;
        }

        .form-label {
          display: block;
          margin-bottom: 0.5rem;
          font-weight: 600;
          color: #374151;
          font-size: 0.95rem;
        }

        .form-select {
          width: 100%;
          padding: 1.25rem;
          border: 2px solid rgba(209, 213, 219, 0.3);
          border-radius: 12px;
          background: rgba(255, 255, 255, 0.9);
          backdrop-filter: blur(10px);
          font-family: inherit;
          font-size: 1rem;
          transition: all 0.3s ease;
          outline: none;
          cursor: pointer;
        }

        .form-select:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
          background: white;
        }

        .assignment-info {
          background: rgba(59, 130, 246, 0.1);
          border: 1px solid rgba(59, 130, 246, 0.2);
          border-radius: 12px;
          padding: 1rem;
          margin-top: 1rem;
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .info-icon {
          width: 32px;
          height: 32px;
          background: rgba(59, 130, 246, 0.2);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #2563eb;
          flex-shrink: 0;
        }

        .info-text {
          color: #1d4ed8;
          font-size: 0.95rem;
          font-weight: 500;
        }

        .file-upload-area {
          border: 2px dashed rgba(102, 126, 234, 0.3);
          border-radius: 16px;
          padding: 3rem;
          text-align: center;
          transition: all 0.3s ease;
          cursor: pointer;
          background: rgba(102, 126, 234, 0.02);
          position: relative;
        }

        .file-upload-area:hover, .file-upload-area.drag-active {
          border-color: #667eea;
          background: rgba(102, 126, 234, 0.05);
          transform: translateY(-2px);
        }

        .file-upload-icon {
          width: 80px;
          height: 80px;
          background: rgba(102, 126, 234, 0.1);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto 1.5rem;
          color: #667eea;
        }

        .file-upload-text {
          color: #374151;
          font-weight: 600;
          font-size: 1.2rem;
          margin-bottom: 0.5rem;
        }

        .file-upload-hint {
          color: #6b7280;
          font-size: 1rem;
          margin-bottom: 1.5rem;
        }

        .file-upload-button {
          background: linear-gradient(45deg, #667eea 30%, #764ba2 90%);
          color: white;
          border: none;
          padding: 1rem 2rem;
          border-radius: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 4px 16px rgba(102, 126, 234, 0.3);
        }

        .file-upload-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
        }

        .selected-file {
          background: rgba(16, 185, 129, 0.1);
          border: 2px solid rgba(16, 185, 129, 0.3);
          padding: 2rem;
          border-radius: 16px;
          display: flex;
          align-items: center;
          gap: 1.5rem;
          color: #059669;
        }

        .file-icon {
          width: 60px;
          height: 60px;
          background: rgba(16, 185, 129, 0.2);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .file-info {
          flex: 1;
        }

        .file-name {
          font-weight: 700;
          font-size: 1.1rem;
          margin-bottom: 0.5rem;
        }

        .file-size {
          font-size: 0.95rem;
          opacity: 0.8;
        }

        .remove-file {
          background: none;
          border: none;
          color: #ef4444;
          cursor: pointer;
          padding: 0.75rem;
          border-radius: 8px;
          transition: background 0.2s ease;
        }

        .remove-file:hover {
          background: rgba(239, 68, 68, 0.1);
        }

        .submit-btn {
          width: 100%;
          padding: 1.5rem 2rem;
          background: linear-gradient(45deg, #10b981 30%, #059669 90%);
          color: white;
          border: none;
          border-radius: 16px;
          font-size: 1.1rem;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          box-shadow: 0 6px 25px rgba(16, 185, 129, 0.4);
          margin-top: 2rem;
        }

        .submit-btn:hover:not(:disabled) {
          transform: translateY(-3px);
          box-shadow: 0 12px 35px rgba(16, 185, 129, 0.5);
        }

        .submit-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
          transform: none;
        }

        .btn-loading-spinner {
          width: 24px;
          height: 24px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top: 3px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }

        .snackbar {
          position: fixed;
          bottom: 2rem;
          left: 50%;
          transform: translateX(-50%);
          padding: 1rem 2rem;
          border-radius: 12px;
          color: white;
          font-weight: 500;
          box-shadow: 0 8px 32px rgba(0,0,0,0.3);
          backdrop-filter: blur(10px);
          z-index: 1000;
          animation: slideInFromBottom 0.3s ease-out;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          max-width: 400px;
        }

        .snackbar.success {
          background: rgba(16, 185, 129, 0.9);
        }

        .snackbar.error {
          background: rgba(239, 68, 68, 0.9);
        }

        .snackbar.warning {
          background: rgba(245, 158, 11, 0.9);
        }

        .snackbar.info {
          background: rgba(59, 130, 246, 0.9);
        }

        .snackbar-close {
          background: none;
          border: none;
          color: white;
          cursor: pointer;
          padding: 0.25rem;
          border-radius: 4px;
          opacity: 0.8;
        }

        .snackbar-close:hover {
          opacity: 1;
          background: rgba(255, 255, 255, 0.1);
        }

        /* Mobile responsiveness */
        @media (max-width: 768px) {
          .assignment-submit-container {
            padding: 1rem 0.5rem;
          }
          
          .form-container {
            padding: 2rem 1.5rem;
          }

          .file-upload-area {
            padding: 2rem;
          }

          .selected-file {
            flex-direction: column;
            text-align: center;
          }

          .snackbar {
            left: 1rem;
            right: 1rem;
            transform: none;
            max-width: none;
          }
        }
      `}</style>

      <div className="pulse-element"></div>

      <div className="submit-wrapper">
        {/* Header Section */}
        <div className="header-section">
          <div className="header-chip">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
            </svg>
            Student Portal
          </div>

          <h1 className="main-title">
            <span className="gradient-text">Submit</span> Assignment
          </h1>

          <p className="subtitle">
            Upload your completed assignment for review
          </p>
        </div>

        {/* Form Container */}
        <div className="form-container">
          <div className="form-title">
            <div className="form-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              </svg>
            </div>
            <div className="form-title-text">Assignment Submission</div>
          </div>

          {fetchingAssignments ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <div className="loading-text">Loading assignments...</div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Select Assignment *</label>
                <select
                  className="form-select"
                  value={selectedAssignment}
                  onChange={(e) => setSelectedAssignment(e.target.value)}
                  required
                >
                  <option value="">-- Choose Assignment --</option>
                  {assignments.map((assignment) => (
                    <option key={assignment._id} value={assignment._id}>
                      {assignment.title} (Due: {formatDate(assignment.dueDate)})
                    </option>
                  ))}
                </select>

                {selectedAssignment && (
                  <div className="assignment-info">
                    <div className="info-icon">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M11,9H13V7H11M12,20C7.59,20 4,16.41 4,12C4,7.59 7.59,4 12,4C16.41,4 20,7.59 20,12C20,16.41 16.41,20 12,20M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M11,17H13V11H11V17Z" />
                      </svg>
                    </div>
                    <div className="info-text">
                      Due: {formatDate(getSelectedAssignment()?.dueDate)} •
                      Make sure to submit before the deadline
                    </div>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label className="form-label">Upload File *</label>
                {!file ? (
                  <div
                    className={`file-upload-area ${dragActive ? 'drag-active' : ''}`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => document.getElementById('file-input').click()}
                  >
                    <div className="file-upload-icon">
                      <svg width="36" height="36" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                      </svg>
                    </div>
                    <div className="file-upload-text">Drop your assignment here</div>
                    <div className="file-upload-hint">or click to browse files</div>
                    <button type="button" className="file-upload-button">
                      Choose File
                    </button>
                  </div>
                ) : (
                  <div className="selected-file">
                    <div className="file-icon">
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M14,2H6A2,2 0 0,0 4,4V20A2,2 0 0,0 6,22H18A2,2 0 0,0 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
                      </svg>
                    </div>
                    <div className="file-info">
                      <div className="file-name">{file.name}</div>
                      <div className="file-size">{formatFileSize(file.size)}</div>
                    </div>
                    <button
                      type="button"
                      className="remove-file"
                      onClick={() => setFile(null)}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
                      </svg>
                    </button>
                  </div>
                )}
                <input
                  id="file-input"
                  type="file"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  required
                />
              </div>

              <button
                type="submit"
                className="submit-btn"
                disabled={loading || !selectedAssignment || !file}
              >
                {loading ? (
                  <>
                    <div className="btn-loading-spinner"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M2,12A10,10 0 0,1 12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22A10,10 0 0,1 2,12M10,17L18,9L16.59,7.58L10,14.17L7.41,11.59L6,13L10,17Z" />
                    </svg>
                    Submit Assignment
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Snackbar Notification */}
      {snackbar.open && (
        <div className={`snackbar ${snackbar.severity}`}>
          <span>{snackbar.message}</span>
          <button className="snackbar-close" onClick={closeSnackbar}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19,6.41L17.59,5L12,10.59L6.41,5L5,6.41L10.59,12L5,17.59L6.41,19L12,13.41L17.59,19L19,17.59L13.41,12L19,6.41Z" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default StudentAssignmentSubmit;
